import React from 'react';
import { motion } from 'framer-motion';
import { Heart, MoreHorizontal, ExternalLink, Instagram, Facebook, Youtube, Twitter } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useData } from '../context/DataContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default placeholder for Instagram/video content
const DEFAULT_VIDEO_PLACEHOLDER = "https://images.pexels.com/photos/3052361/pexels-photo-3052361.jpeg?auto=compress&cs=tinysrgb&w=600";

const getPlatformIcon = (platform) => {
  switch (platform) {
    case 'instagram': return <Instagram className="w-3 h-3" />;
    case 'facebook': return <Facebook className="w-3 h-3" />;
    case 'youtube': return <Youtube className="w-3 h-3" />;
    case 'twitter': return <Twitter className="w-3 h-3" />;
    default: return null;
  }
};

const getPlatformColor = (platform) => {
  switch (platform) {
    case 'instagram': return 'bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500';
    case 'facebook': return 'bg-blue-600';
    case 'youtube': return 'bg-red-600';
    case 'twitter': return 'bg-sky-500';
    case 'tiktok': return 'bg-black';
    default: return 'bg-zinc-600';
  }
};

const AdCard = ({ ad, onClick, onToggleFavorite, onDelete }) => {
  const { tags: allTags } = useData();
  
  // Use thumbnail if available, otherwise use default placeholder for videos/Instagram
  const getThumbnailUrl = () => {
    if (ad.thumbnail_url) {
      return ad.thumbnail_url.startsWith('/api') ? `${API_URL}${ad.thumbnail_url}` : ad.thumbnail_url;
    }
    // Default image for Instagram or video content without thumbnail
    if (ad.platform === 'instagram' || ad.ad_format === 'video' || ad.ad_format === 'reel') {
      return DEFAULT_VIDEO_PLACEHOLDER;
    }
    return null;
  };
  
  const thumbnailUrl = getThumbnailUrl();
  
  // Check if we're showing a placeholder (no real thumbnail)
  const isPlaceholder = !ad.thumbnail_url && (ad.platform === 'instagram' || ad.ad_format === 'video' || ad.ad_format === 'reel');
  
  // Get assigned tags (user tags, not AI tags)
  const assignedTags = (ad.tag_ids || []).map(tagId => allTags.find(t => t.id === tagId)).filter(Boolean);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -4 }}
      className="masonry-item"
    >
      <div
        data-testid={`ad-card-${ad.id}`}
        onClick={() => onClick?.(ad)}
        className="
          ad-card group cursor-pointer
          bg-zinc-900/50 rounded-xl overflow-hidden
          border border-white/5
        "
      >
        {/* Image container */}
        <div className="relative aspect-auto overflow-hidden">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={ad.title || 'Ad'}
              className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full aspect-video bg-zinc-800 flex items-center justify-center">
              <span className="text-zinc-600 text-sm">No preview</span>
            </div>
          )}
          
          {/* Overlay on hover */}
          <div className="
            absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent
            opacity-0 group-hover:opacity-100 transition-opacity duration-300
          ">
            {/* Actions */}
            <div className="absolute top-3 right-3 flex gap-2">
              <Button
                data-testid={`favorite-btn-${ad.id}`}
                variant="ghost"
                size="icon"
                className={`
                  h-8 w-8 rounded-full backdrop-blur-sm
                  ${ad.is_favorite ? 'bg-red-500/80 text-white' : 'bg-black/40 text-white hover:bg-black/60'}
                `}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite?.(ad);
                }}
              >
                <Heart className={`w-4 h-4 ${ad.is_favorite ? 'fill-current' : ''}`} />
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full bg-black/40 text-white backdrop-blur-sm hover:bg-black/60"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                  {ad.source_url && (
                    <DropdownMenuItem
                      className="text-zinc-300 focus:text-white focus:bg-zinc-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(ad.source_url, '_blank');
                      }}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open original
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="text-red-400 focus:text-red-300 focus:bg-zinc-800"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(ad);
                    }}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Platform badge */}
            {ad.platform && (
              <div className={`
                absolute top-3 left-3 
                px-2 py-1 rounded-full text-white text-xs font-medium
                flex items-center gap-1
                ${getPlatformColor(ad.platform)}
              `}>
                {getPlatformIcon(ad.platform)}
                <span className="capitalize">{ad.platform}</span>
              </div>
            )}
          </div>
          
          {/* Processing indicator */}
          {ad.processing_status === 'processing' && (
            <div className="absolute bottom-3 left-3">
              <div className="px-2 py-1 rounded-full bg-violet-500/80 text-white text-xs font-medium flex items-center gap-1.5 animate-pulse">
                <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Processing...
              </div>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-4">
          {/* Title */}
          {ad.title && (
            <h3 className="font-medium text-zinc-200 line-clamp-1 mb-2">
              {ad.title}
            </h3>
          )}
          
          {/* AI Description */}
          {ad.ai_description && (
            <p className="text-sm text-zinc-400 line-clamp-2 mb-3">
              {ad.ai_description}
            </p>
          )}
          
          {/* Assigned Tags (user-created) */}
          {assignedTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {assignedTags.slice(0, 3).map((tag) => (
                <Badge
                  key={tag.id}
                  className="bg-violet-500/20 text-violet-300 border border-violet-500/30 text-xs"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full mr-1"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </Badge>
              ))}
              {assignedTags.length > 3 && (
                <Badge
                  className="bg-violet-500/10 text-violet-400 text-xs"
                >
                  +{assignedTags.length - 3}
                </Badge>
              )}
            </div>
          )}
          
          {/* AI Tags (suggested) */}
          {(ad.ai_tags && ad.ai_tags.length > 0 && assignedTags.length === 0) && (
            <div className="flex flex-wrap gap-1.5">
              {ad.ai_tags.slice(0, 3).map((tag, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="bg-zinc-800 text-zinc-400 hover:bg-zinc-700 text-xs"
                >
                  {tag}
                </Badge>
              ))}
              {ad.ai_tags.length > 3 && (
                <Badge
                  variant="secondary"
                  className="bg-zinc-800 text-zinc-500 text-xs"
                >
                  +{ad.ai_tags.length - 3}
                </Badge>
              )}
            </div>
          )}
          
          {/* Inbox indicator */}
          {ad.is_inbox && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-500">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Unsorted
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AdCard;
